import React, { useState } from 'react';
import { useParams, Navigate, Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/layout/Navbar';
import PageHeader from '../components/layout/PageHeader';
import GuidedTour from '../components/tour/GuidedTour';
import { BarChart2, QrCode, Clock, CheckCircle, TrendingUp, Award, Download, ChevronLeft } from 'lucide-react';
import ReportDownload from '../components/ReportDownload';
import UnverifiedExplainer from '../components/UnverifiedExplainer';
import './SponsorPages.css';
import { TIER_COLORS as tierColors } from '../config/tiers';

const PERIODS = [
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: '90d', label: '90 days' },
  { value: 'all', label: 'All time' },
];

// ── Dependency-free dashboard visuals (no chart library / no rebuild) ──
function TrendChart({ series }) {
  if (!series || series.length === 0) return null;
  const W = 600, H = 150, pad = 22;
  const max = Math.max(...series.map(d => d.redemptions), 1);
  const n = series.length;
  const slot = (W - pad * 2) / n;
  const barW = Math.max(2, Math.min(slot * 0.7, 20));
  const fmt = (s) => new Date(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  return (
    <div className="sponsor-section">
      <h2 className="sponsor-section-heading"><TrendingUp size={18} /> Redemptions over time</h2>
      <div className="card" style={{ padding: '18px 16px' }}>
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none" style={{ display: 'block' }}>
          {series.map((d, i) => {
            const h = (d.redemptions / max) * (H - pad * 2);
            const x = pad + i * slot + (slot - barW) / 2;
            const y = H - pad - h;
            return (
              <rect key={i} x={x} y={y} width={barW} height={Math.max(h, 1)} rx={2} fill="var(--green)" opacity={0.85}>
                <title>{`${fmt(d.day)}: ${d.redemptions} redemption${d.redemptions === 1 ? '' : 's'} · £${Number(d.spend).toFixed(0)}`}</title>
              </rect>
            );
          })}
          <line x1={pad} y1={H - pad} x2={W - pad} y2={H - pad} stroke="var(--border)" strokeWidth="1" />
        </svg>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-secondary)', marginTop: 6 }}>
          <span>{fmt(series[0].day)}</span>
          <span>{fmt(series[series.length - 1].day)}</span>
        </div>
      </div>
    </div>
  );
}

function Funnel({ totals }) {
  const codes = totals.codes || 0;
  const reds = totals.redemptions || 0;
  if (codes === 0 && reds === 0) return null;
  const conv = codes > 0 ? Math.round((reds / codes) * 100) : 0;
  const stages = [
    { label: 'Codes claimed', n: codes, w: 100, color: 'var(--gold)' },
    { label: 'Redeemed at checkout', n: reds, w: codes > 0 ? Math.max(6, (reds / codes) * 100) : 100, color: 'var(--green)' },
  ];
  return (
    <div className="sponsor-section">
      <h2 className="sponsor-section-heading"><QrCode size={18} /> Claim → redeem funnel</h2>
      <div className="card" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {stages.map((s, i) => (
          <div key={i}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13.5, marginBottom: 4 }}>
              <span style={{ fontWeight: 700 }}>{s.label}</span>
              <span style={{ color: 'var(--text-secondary)' }}>{s.n}</span>
            </div>
            <div style={{ height: 16, background: 'var(--border)', borderRadius: 6, overflow: 'hidden' }}>
              <div style={{ width: `${s.w}%`, height: '100%', background: s.color, borderRadius: 6 }} />
            </div>
          </div>
        ))}
        <p style={{ fontSize: 13.5, color: 'var(--text-secondary)', margin: 0, textAlign: 'center' }}>
          <strong style={{ color: 'var(--green)' }}>{conv}%</strong> of claimed codes were redeemed at checkout
          {totals.spend > 0 && <> — £{totals.spend.toFixed(0)} tracked spend</>}.
        </p>
      </div>
    </div>
  );
}

function Leaderboard({ breakdown }) {
  const rows = [...breakdown]
    .map(r => ({ name: r.business_name, spend: parseFloat(r.total_spend || 0), reds: parseInt(r.total_redemptions || 0) }))
    .filter(r => r.spend > 0)
    .sort((a, b) => b.spend - a.spend);
  if (rows.length === 0) return null;
  const max = Math.max(...rows.map(r => r.spend), 1);
  return (
    <div className="sponsor-section">
      <h2 className="sponsor-section-heading"><Award size={18} /> Sponsor leaderboard — tracked spend</h2>
      <div className="card" style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {rows.map((r, i) => (
          <div key={i}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13.5, marginBottom: 4 }}>
              <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{i + 1}. {r.name}</span>
              <span style={{ color: 'var(--text-secondary)' }}>£{r.spend.toFixed(0)} · {r.reds} redemptions</span>
            </div>
            <div style={{ height: 10, background: 'var(--border)', borderRadius: 6, overflow: 'hidden' }}>
              <div style={{ width: `${Math.max(3, (r.spend / max) * 100)}%`, height: '100%', background: 'var(--green)', borderRadius: 6 }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const SponsorAttributionPage = () => {
  const { clubSlug } = useParams();
  const location = useLocation(); // UC-B5: return path for /login
  const { user, loading: authLoading, getMembership } = useAuth();
  const membership = getMembership(clubSlug);
  const [period, setPeriod] = useState('30d');
  const [sponsorId, setSponsorId] = useState(''); // '' = all sponsors (admin picker)

  // Fetch club data for Navbar
  const { data: club } = useQuery({
    queryKey: ['club', clubSlug],
    queryFn: () => axios.get(`/clubs/${clubSlug}`).then(r => r.data),
  });

  // The logged-in sponsor's own record (business name + logo) for the header.
  const { data: me } = useQuery({
    queryKey: ['sponsor-me', clubSlug],
    queryFn: () => axios.get(`/clubs/${clubSlug}/sponsors/me`).then(r => r.data),
    enabled: !!membership,
  });

  const isAdmin = membership?.role === 'admin';

  // Fetch attribution report (optionally scoped to one sponsor for admins)
  const { data, isLoading } = useQuery({
    queryKey: ['sponsor-attribution', clubSlug, period, sponsorId],
    queryFn: () => axios.get(`/clubs/${clubSlug}/sponsors/report/redemptions?period=${period}${sponsorId ? `&sponsorId=${sponsorId}` : ''}`).then(r => r.data),
    enabled: !!user && !!membership,
    staleTime: 30000,
  });

  // Full sponsor list for the picker — kept separate from the report so the
  // dropdown options don't collapse when a single sponsor is selected.
  const { data: sponsorList = [] } = useQuery({
    queryKey: ['club-sponsors-list', clubSlug],
    queryFn: () => axios.get(`/clubs/${clubSlug}/sponsors`).then(r => r.data),
    enabled: !!user && isAdmin,
    staleTime: 60000,
  });

  if (authLoading) return <div className="page-loading"><div className="spinner" style={{ width: 40, height: 40 }} /></div>;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  if (membership && membership.role !== 'sponsor' && membership.role !== 'admin') {
    return <Navigate to={`/${clubSlug}`} replace />;
  }
  // W-2: an unverified club's admin gets the revenue case + a link to the
  // verification form instead of an empty dashboard over a 403.
  if (club && !club.verified_at) {
    return (
      <div className="sponsor-page">
        <Navbar club={club} />
        <PageHeader icon={BarChart2} title="Sponsorship" subtitle={club.name} />
        <UnverifiedExplainer clubSlug={clubSlug} club={club} what="Attribution" />
      </div>
    );
  }

  const breakdown = data?.breakdown || [];
  const recent = data?.recentRedemptions || [];
  // A sponsor viewing their own dashboard doesn't need the Business column (it's always them).
  const isSponsorView = membership?.role === 'sponsor';

  // Calculate totals from breakdown (v2: full value metrics)
  const totals = breakdown.reduce((acc, row) => ({
    redemptions: acc.redemptions + parseInt(row.total_redemptions || 0),
    members: acc.members + parseInt(row.unique_members || 0),
    spend: acc.spend + parseFloat(row.total_spend || 0),
    discount: acc.discount + parseFloat(row.total_discount || 0),
    net: acc.net + parseFloat(row.total_net || 0),
    codes: acc.codes + parseInt(row.codes_generated || 0),
  }), { redemptions: 0, members: 0, spend: 0, discount: 0, net: 0, codes: 0 });

  const avgBasket = totals.redemptions > 0 ? totals.spend / totals.redemptions : 0;

  // Sponsorship cost pro-rated to the selected window so it lines up with the
  // period-scoped revenue. Each sponsor's daily rate = cost / term length;
  // summed across sponsors and multiplied by the window length.
  const DAY_MS = 86400000;
  const periodDays = period === '7d' ? 7 : period === '30d' ? 30 : period === '90d' ? 90
    : (data?.series?.length
        ? Math.max(1, Math.round((Date.now() - new Date(data.series[0].day).getTime()) / DAY_MS) + 1)
        : 30);
  const totalDailyCost = breakdown.reduce((sum, row) => {
    const cost = parseFloat(row.sponsorship_cost || 0);
    if (!cost || !row.sponsorship_start || !row.sponsorship_end) return sum;
    const termDays = Math.max(1, Math.round((new Date(row.sponsorship_end) - new Date(row.sponsorship_start)) / DAY_MS));
    return sum + cost / termDays;
  }, 0);
  const hasCost = totalDailyCost > 0;
  const periodCost = totalDailyCost * periodDays;
  const revenue = totals.spend;
  const returnMultiple = hasCost && periodCost > 0 ? revenue / periodCost : null;
  const costPerRedemption = hasCost && totals.redemptions > 0 ? periodCost / totals.redemptions : null;
  const costPerMember = hasCost && totals.members > 0 ? periodCost / totals.members : null;
  const periodLabel = (PERIODS.find(p => p.value === period) || {}).label || '';

  return (
    <div className="sponsor-page">
      <Navbar club={club} />
      {/* Sponsor tour hops here for its "your numbers" step (never auto-starts). */}
      <GuidedTour club={club} surface="attribution" />

      <PageHeader
        icon={TrendingUp}
        logoUrl={me?.logo_url || undefined}
        title="Attribution Dashboard"
        subtitle={<>{me?.business_name || `${user.firstName} ${user.lastName}`} · {club?.name || clubSlug}</>}
      >
        {/* B-46: admins arrive from Admin → Reports → Sponsorship now; sponsors never see this. */}
        {isAdmin && (
          <Link to={`/${clubSlug}/admin?tab=report-sponsorship`} className="btn btn-ghost btn-sm">
            <ChevronLeft size={14} /> Admin
          </Link>
        )}
      </PageHeader>

      <div className="sponsor-content">
        {/* Sponsor picker — admins with more than one sponsor */}
        {isAdmin && sponsorList.length > 1 && (
          <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Sponsor</span>
            <select className="input" style={{ maxWidth: 280, width: 'auto' }}
              value={sponsorId} onChange={e => setSponsorId(e.target.value)}>
              <option value="">All sponsors</option>
              {sponsorList.map(s => (
                <option key={s.id} value={s.id}>{s.business_name}</option>
              ))}
            </select>
          </div>
        )}

        {/* Period selector */}
        <div className="pill-group">
          {PERIODS.map(p => (
            <button key={p.value}
              className={`pill-btn ${period === p.value ? 'active' : ''}`}
              onClick={() => setPeriod(p.value)}>
              {p.label}
            </button>
          ))}
        </div>

        {isLoading && <div className="page-loading" style={{ minHeight: '20vh' }}><div className="spinner" /></div>}

        {!isLoading && data && (
          <>
            {/* ── Sponsorship performance — the headline insight, top of page ── */}
            <div className="sponsor-section">
              <h2 className="sponsor-section-heading"><TrendingUp size={18} /> Sponsorship performance · {periodLabel}</h2>
              <div className="card" style={{ padding: '20px 22px' }}>
                {hasCost ? (
                  <>
                    <div className="perf-figures">
                      <div className="perf-fig">
                        <div className="perf-fig-label">Sponsorship cost</div>
                        <div className="perf-fig-num">£{periodCost.toFixed(0)}</div>
                        <div className="perf-fig-sub">this period</div>
                      </div>
                      <div className="perf-arrow">→</div>
                      <div className="perf-fig">
                        <div className="perf-fig-label">Revenue attributed</div>
                        <div className="perf-fig-num" style={{ color: 'var(--green)' }}>£{revenue.toFixed(0)}</div>
                        <div className="perf-fig-sub">via QR redemptions</div>
                      </div>
                      <div className="perf-fig">
                        <div className="perf-fig-label">Cost per acquisition</div>
                        <div className="perf-fig-num">{costPerRedemption != null ? `£${costPerRedemption.toFixed(2)}` : '—'}</div>
                        <div className="perf-fig-sub">per redemption</div>
                      </div>
                      {returnMultiple != null && (
                        <div className="perf-return">
                          <div className="perf-fig-label">Return on spend</div>
                          <div className="perf-return-num">{returnMultiple.toFixed(1)}×</div>
                          <div className="perf-fig-sub">revenue per £1 spent</div>
                        </div>
                      )}
                    </div>
                    <div style={{ height: 1, background: 'var(--border)', margin: '18px 0' }} />
                    <div style={{ display: 'grid', gap: 12, fontSize: 14, color: 'var(--text-primary)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                        <span><strong>{totals.redemptions}</strong> redemptions</span>
                        <span style={{ color: 'var(--text-secondary)' }}>transactions</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                        <span><strong>{totals.members}</strong> members reached</span>
                        <span style={{ color: 'var(--text-secondary)' }}>unique customers acquired</span>
                      </div>
                    </div>
                    {totals.discount > 0 && (
                      <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '18px 0 0', lineHeight: 1.5 }}>
                        Plus <strong>£{totals.discount.toFixed(0)}</strong> of member discounts honoured — for every £1 of discount, members spent £{(totals.spend / totals.discount).toFixed(2)} with you.
                      </p>
                    )}
                  </>
                ) : (
                  <div style={{ textAlign: 'center', padding: '6px 0' }}>
                    <p style={{ fontSize: 15, color: 'var(--text-primary)', margin: '0 0 6px', fontWeight: 700 }}>
                      Add your sponsorship cost to see your return on spend.
                    </p>
                    <p style={{ fontSize: 13.5, color: 'var(--text-secondary)', margin: '0 auto', maxWidth: 480 }}>
                      So far this period: <strong>£{revenue.toFixed(0)}</strong> of customer spend attributed across{' '}
                      <strong>{totals.redemptions}</strong> redemptions and <strong>{totals.members}</strong> members.
                      {membership?.role === 'sponsor'
                        ? ' Add your package cost & dates in Sponsorship settings to see your return on spend.'
                        : ' Add each sponsor\'s package cost & dates in Admin → Sponsors to see return on spend.'}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Stats cards — v2 enhanced metrics */}
            <div className="sponsor-stats-grid" data-tour="attribution-stats">
              <div className="stat-card">
                <div className="stat-value" style={{ color: 'var(--green)' }}>{totals.redemptions}</div>
                <div className="stat-label">Total Redemptions</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{totals.members}</div>
                <div className="stat-label">Unique Members</div>
              </div>
              <div className="stat-card">
                <div className="stat-value" style={{ color: 'var(--gold)' }}>
                  {totals.spend ? `£${totals.spend.toFixed(0)}` : '£0'}
                </div>
                <div className="stat-label">Total Customer Spend</div>
              </div>
              <div className="stat-card">
                <div className="stat-value" style={{ color: 'var(--success)' }}>
                  {totals.discount ? `£${totals.discount.toFixed(0)}` : '£0'}
                </div>
                <div className="stat-label">Discount Honoured</div>
              </div>
              <div className="stat-card">
                <div className="stat-value" style={{ color: 'var(--green)' }}>
                  {avgBasket ? `£${avgBasket.toFixed(2)}` : '—'}
                </div>
                <div className="stat-label">Avg Basket Size</div>
              </div>
              <div className="stat-card">
                <div className="stat-value" style={{ color: 'var(--gold)' }}>
                  {costPerRedemption != null ? `£${costPerRedemption.toFixed(2)}` : '—'}
                </div>
                <div className="stat-label">Cost / Redemption</div>
              </div>
            </div>

            {/* Trend, funnel & leaderboard */}
            <TrendChart series={data.series} />
            <Funnel totals={totals} />
            <Leaderboard breakdown={breakdown} />

            {/* Breakdown by sponsor/offer */}
            {breakdown.length > 0 && (
              <div className="sponsor-section">
                <h2 className="sponsor-section-heading"><BarChart2 size={18} /> Attribution by Offer</h2>
                <div className="report-table-wrap card" style={{ padding: 0, overflowX: 'auto' }}>
                  <table className="report-table">
                    <thead>
                      <tr>
                        {!isSponsorView && <th>Business</th>}
                        <th>Offer</th>
                        <th>Redemptions</th>
                        <th className="col-secondary">Members</th>
                        <th>Spend</th>
                        <th className="col-secondary">Discount</th>
                        <th className="col-secondary">Avg Basket</th>
                      </tr>
                    </thead>
                    <tbody>
                      {breakdown.map((row, i) => (
                        <tr key={i}>
                          {!isSponsorView && <td><strong>{row.business_name}</strong></td>}
                          <td>
                            {row.offer_title || '—'}
                            {row.offer_value && <span className="table-sub">{row.offer_value}</span>}
                          </td>
                          <td><strong style={{ color: 'var(--green)' }}>{row.total_redemptions}</strong></td>
                          <td className="col-secondary">{row.unique_members}</td>
                          <td>{row.total_spend ? `£${parseFloat(row.total_spend).toFixed(2)}` : '—'}</td>
                          <td className="col-secondary" style={{ color: 'var(--success)' }}>{row.total_discount ? `£${parseFloat(row.total_discount).toFixed(2)}` : '—'}</td>
                          <td className="col-secondary">{row.avg_basket ? `£${parseFloat(row.avg_basket).toFixed(2)}` : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Download — same period and the same role scoping as the screen,
                so the file always agrees with what's shown above it. */}
            <div className="sponsor-section">
              <h2 className="sponsor-section-heading"><Download size={18} /> Download this report</h2>
              <ReportDownload
                url={`/clubs/${clubSlug}/reports/sponsorship/export`}
                params={{ period, sponsorId: sponsorId || undefined }}
                fallbackName="sponsorship"
                csvSheets={[{ index: 0, label: 'CSV — by sponsor' }, { index: 1, label: 'CSV — redemptions' }]}
                note="Excel contains both sheets. The download covers every redemption in the period, not just the recent ones listed below."
              />
            </div>

            {/* Recent redemptions */}
            {recent.length > 0 && (
              <div className="sponsor-section">
                <h2 className="sponsor-section-heading"><Clock size={18} /> Recent Redemptions</h2>
                <div className="members-list">
                  {recent.map((r, i) => (
                    <div key={r.id || i} className="member-row card">
                      <div className="member-avatar" style={{ background: 'rgba(45,122,79,0.08)', color: 'var(--success)' }}>
                        <CheckCircle size={16} />
                      </div>
                      <div className="member-info">
                        <strong>{r.member_name || 'Club member'}</strong>
                        <span>
                          {r.offer_title} · {r.business_name}
                          {r.code && (
                            <> · <span style={{ fontFamily: 'monospace', letterSpacing: '0.04em' }}>#{String(r.code).slice(-8).toUpperCase()}</span></>
                          )}
                        </span>
                      </div>
                      <div style={{ textAlign: 'right', minWidth: 80 }}>
                        {r.transaction_value && (
                          <div style={{ fontFamily: "'Nunito', sans-serif", fontSize: 16, fontWeight: 800, color: 'var(--green)' }}>
                            £{parseFloat(r.transaction_value).toFixed(2)}
                          </div>
                        )}
                        {r.discount_value && (
                          <div style={{ fontSize: 12, color: 'var(--success)' }}>
                            −£{parseFloat(r.discount_value).toFixed(2)} disc.
                          </div>
                        )}
                      </div>
                      <span className="member-joined">
                        {new Date(r.redeemed_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                        {' '}
                        {new Date(r.redeemed_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Empty state */}
            {breakdown.length === 0 && (
              <div className="empty-state" style={{ padding: '60px 24px', background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
                <QrCode size={48} style={{ color: 'var(--text-muted)', marginBottom: 16 }} />
                <h3 style={{ marginBottom: 8 }}>No redemptions yet</h3>
                <p style={{ color: 'var(--text-secondary)', fontSize: 14, maxWidth: 400, margin: '0 auto' }}>
                  When club members start using your QR codes, attribution data will appear here.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SponsorAttributionPage;
